import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Trash2, Mail, Phone, Circle } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { REQUEST_STATUSES, statusInfo } from "@/lib/requestStatus";

function fmt(d) {
  try { return new Date(d).toLocaleString("de-CH", { dateStyle: "medium", timeStyle: "short" }); } catch { return ""; }
}

export default function AdminOrderRequests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("alle");

  useEffect(() => {
    api.get("/admin/order-requests").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const markRead = async (id) => {
    await api.patch(`/admin/order-requests/${id}`, { read: true }).catch(() => {});
    setItems((x) => x.map((i) => (i.id === id ? { ...i, read: true } : i)));
  };
  const setStatus = async (id, status) => {
    await api.patch(`/admin/order-requests/${id}`, { status }).catch(() => toast.error("Fehler"));
    setItems((x) => x.map((i) => (i.id === id ? { ...i, status } : i)));
    toast.success("Status aktualisiert");
  };
  const del = async (id) => {
    await api.delete(`/admin/order-requests/${id}`).catch(() => toast.error("Fehler"));
    setItems((x) => x.filter((i) => i.id !== id));
    toast.success("Anfrage gelöscht");
  };

  const shown = useMemo(() => (filter === "alle" ? items : items.filter((i) => (i.status || "neu") === filter)), [items, filter]);

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
        <h1 className="font-serif text-4xl font-light">Bestellanfragen</h1>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-52 h-10 rounded-md bg-white" data-testid="order-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">Alle Status</SelectItem>
            {REQUEST_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {loading ? <p className="text-[#9A86B4]">Laden…</p> : shown.length === 0 ? (
        <p className="text-[#9A86B4]">Keine Bestellanfragen in dieser Ansicht.</p>
      ) : (
        <div className="space-y-4">
          {shown.map((r) => {
            const si = statusInfo(r.status);
            return (
              <div key={r.id} className={`bg-white border p-5 ${r.read ? "border-[#E7D8C6]" : "border-brand"}`} data-testid={`order-request-${r.id}`} onMouseEnter={() => !r.read && markRead(r.id)}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {!r.read && <Circle className="w-2 h-2 fill-brand text-brand" />}
                      <span className="font-medium">{r.customer_name}</span>
                      <Badge className={`rounded-md ${r.kind === "bestellung" ? "bg-[#D85CA4]" : "bg-[#632A8E]"}`}>
                        {r.kind === "bestellung" ? "Bestellanfrage" : "Anfrage"}
                      </Badge>
                      <span className={`text-xs px-2.5 py-1 rounded-full border ${si.cls}`} data-testid={`order-badge-${r.id}`}>{si.label}</span>
                    </div>
                    <div className="text-sm text-[#6E5A82] mt-1">Werk: {r.artwork_title}</div>
                    <div className="text-xs text-[#9A86B4] mt-1">{fmt(r.created_at)}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={r.status || "neu"} onValueChange={(v) => setStatus(r.id, v)}>
                      <SelectTrigger className="w-52 h-9 rounded-md" data-testid={`order-status-${r.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {REQUEST_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button size="icon" variant="ghost" onClick={() => del(r.id)} data-testid={`delete-order-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
                {r.message && <p className="mt-3 text-[#3A1E55] whitespace-pre-wrap">{r.message}</p>}
                <div className="mt-4 flex flex-wrap gap-4 text-sm">
                  <a href={`mailto:${r.customer_email}`} className="inline-flex items-center gap-2 text-ink hover:underline"><Mail className="w-4 h-4" /> {r.customer_email}</a>
                  {r.phone && <a href={`tel:${r.phone}`} className="inline-flex items-center gap-2 text-ink hover:underline"><Phone className="w-4 h-4" /> {r.phone}</a>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
